import { useCallback, useEffect } from 'react'

import { useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import create from 'zustand'

import { ClusterCluster, OrgOrganization, UserUserResp } from '@clinic/client'
import { getClusterInfo, getOrgInfo, getUserProfile } from '~/api'
import {
  ClusterDeployType,
  ClusterDeployTypeV2,
  isPremiumLikeDeployTypeV2,
  isPremiumLineResourcePoolDeployTypeV2
} from '~/utils/cluster-status'
import * as telemetry from '~/utils/telemetry'
import { mixpanel } from '~/utils/telemetry'

/////////////////
// cur user

type UserState = {
  user?: UserUserResp

  updateUserFromRemote: () => Promise<void>
  updateUser: (newUser: UserUserResp) => void
  clearUser: () => void
}

export const useUserStore = create<UserState>((set, get) => ({
  user: undefined,

  updateUserFromRemote: async () => {
    const { updateUser } = get()
    const res = await getUserProfile()
    updateUser(res.data)
  },
  updateUser: (newUser: UserUserResp) => {
    telemetry.registerUser(newUser)
    set({ user: newUser })
  },
  clearUser: () => {
    set({ user: undefined })
  }
}))

/////////////////
// cur org

export enum OrgType {
  OnPrem = 'op',
  Cloud = 'cloud'
}

interface OrgEx extends OrgOrganization {
  isAdmin: boolean
  isAnalyst: boolean

  orgType: OrgType
  isOP: boolean
  isCloud: boolean
}

function convertOrg(org: OrgOrganization): OrgEx {
  const roleNames = org.roleNames ?? []
  const isAdmin = roleNames.includes('Admin')
  const isAnalyst =
    !roleNames.includes('Admin') && roleNames.includes('Analyst')
  const orgType: OrgType = (org.type || 'op') as OrgType
  const isOP = orgType === 'op'
  const isCloud = orgType === 'cloud'

  return {
    ...org,
    isAdmin,
    isAnalyst,
    orgType,
    isOP,
    isCloud
  }
}

type OrgState = {
  curOrg?: OrgEx

  updateCurOrg: (newOrg: OrgOrganization) => void
  clearCurOrg: () => void
}

const useOrgStore = create<OrgState>((set) => ({
  curOrg: undefined,

  updateCurOrg: async (newOrg: OrgOrganization) => {
    const curOrg = convertOrg(newOrg)
    mixpanel.register({ org_id: curOrg.id, org_type: curOrg.orgType })
    set({ curOrg })
  },
  clearCurOrg: () => {
    mixpanel.unregister('org_id')
    mixpanel.unregister('org_type')
    set({ curOrg: undefined })
  }
}))

export const useQueryOrg = (orgId: string) => {
  const { curOrg, clearCurOrg, updateCurOrg } = useOrgStore()
  const navigate = useNavigate()

  const { data: res, error } = useSWR(`/orgs/${orgId}`, () => {
    // console.log('request org info')
    return getOrgInfo(orgId || '')
  })

  useEffect(() => {
    if (res && !curOrg) {
      updateCurOrg(res.data)
    } else if (error) {
      navigate('/orgs')
    }
  }, [res, curOrg, updateCurOrg, error, navigate])

  useEffect(() => {
    return () => {
      // console.log('clear org')
      clearCurOrg()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export const useCurOrg = () => {
  const { curOrg, updateCurOrg } = useOrgStore()

  const reloadCurOrg = useCallback(async () => {
    const res = await getOrgInfo(curOrg?.id || '')
    if (res) {
      updateCurOrg(res.data)
    }
  }, [curOrg?.id, updateCurOrg])

  return {
    curOrg,
    reloadCurOrg
  }
}

/////////////////
// cur cluster

export type ClusterComputed = {
  isStarter: boolean
  isEssential: boolean
  isDedicated: boolean
  isPremium: boolean
  isPremiumResourcePool: boolean
  isByoc: boolean
  isByocResourcePool: boolean
  isResourcePool: boolean

  isServerless: boolean

  isTiupCluster: boolean
  isTiDBOperator: boolean
}

export type ComputedCluster = ClusterCluster & { computed: ClusterComputed }

export const getComputedCluster = (
  cluster: ClusterCluster
): ComputedCluster => {
  const isStarter = cluster.deployTypeV2 === ClusterDeployTypeV2.Starter
  const isEssential = cluster.deployTypeV2 === ClusterDeployTypeV2.Essential
  const isDedicated = cluster.deployTypeV2 === ClusterDeployTypeV2.Dedicated
  const isPremium = isPremiumLikeDeployTypeV2(cluster.deployTypeV2)
  const isPremiumResourcePool = isPremiumLineResourcePoolDeployTypeV2(
    cluster.deployTypeV2
  )
  const isByoc = cluster.deployTypeV2 === ClusterDeployTypeV2.Byoc
  const isByocResourcePool =
    cluster.deployTypeV2 === ClusterDeployTypeV2.ByocResourcePool
  const isResourcePool = isPremiumResourcePool || isByocResourcePool

  const isTiupCluster = cluster.deployTypeV2 === ClusterDeployTypeV2.TiupCluster
  const isTiDBOperator =
    cluster.deployTypeV2 === ClusterDeployTypeV2.TiDBOperator

  const isServerless =
    cluster.deployType === ClusterDeployType.Shared ||
    !(
      isDedicated ||
      isPremium ||
      isByoc ||
      isResourcePool ||
      isTiupCluster ||
      isTiDBOperator
    )

  return {
    ...cluster,
    computed: {
      isStarter,
      isEssential,
      isDedicated,
      isPremium,
      isPremiumResourcePool,
      isByoc,
      isByocResourcePool,
      isResourcePool,

      isServerless,
      isTiupCluster,
      isTiDBOperator
    }
  }
}

type ClusterState = {
  curCluster?: ComputedCluster

  updateCurCluster: (newCluster: ClusterCluster) => void
  clearCurCluster: () => void
}

const useClusterStore = create<ClusterState>((set) => ({
  curCluster: undefined,

  updateCurCluster: (newCluster: ClusterCluster) => {
    mixpanel.register({ cluster_id: newCluster.id })
    const computedCluster = getComputedCluster(newCluster)
    set({
      curCluster: computedCluster
    })
  },
  clearCurCluster: () => {
    mixpanel.unregister('cluster_id')
    set({ curCluster: undefined })
  }
}))

export const useQueryCluster = (orgId: string, cid: string) => {
  const { curCluster, clearCurCluster, updateCurCluster } = useClusterStore()
  const navigate = useNavigate()

  const { data: res, error } = useSWR(`/orgs/${orgId}/clusters/${cid}`, () => {
    // console.log('request cluster info')
    return getClusterInfo(orgId || '', cid || '')
  })

  useEffect(() => {
    if (res && !curCluster) {
      updateCurCluster(res.data)
    } else if (error) {
      navigate(`/orgs/${orgId}`)
    }
  }, [orgId, res, curCluster, updateCurCluster, error, navigate])

  useEffect(() => {
    return () => {
      // console.log('clear cluster')
      clearCurCluster()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export const useCurCluster = () => {
  const { curOrg } = useOrgStore()
  const { curCluster, updateCurCluster } = useClusterStore()

  const reloadCurCluster = useCallback(async () => {
    const res = await getClusterInfo(curOrg?.id || '', curCluster?.id || '')
    if (res) {
      updateCurCluster(res.data)
    }
  }, [curOrg?.id, curCluster?.id, updateCurCluster])

  return {
    curCluster,
    reloadCurCluster
  }
}
